import React from 'react';
import { Navigate } from 'react-router-dom';
import { useUserRole, UserRole } from '../hooks/useUserRole';
import { Progress } from '@backstage/core-components';

interface RoleBasedRouteProps {
  children: React.ReactElement;
  allowedRoles?: UserRole[];
  requireSecTeam?: boolean;
  requireAdmin?: boolean;
  redirectTo?: string;
}

/**
 * Component to restrict access to routes based on user role
 *
 * Usage:
 * <RoleBasedRoute allowedRoles={['admin']}>
 *   <AdminPage />
 * </RoleBasedRoute>
 *
 * Or use flags:
 * <RoleBasedRoute requireSecTeam>
 *   <SecurityPage />
 * </RoleBasedRoute>
 */
export const RoleBasedRoute = ({
  children,
  allowedRoles,
  requireSecTeam,
  requireAdmin,
  redirectTo = '/',
}: RoleBasedRouteProps) => {
  const { role, loading, isSecTeam, isAdmin } = useUserRole();

  if (loading) {
    return <Progress />;
  }

  // Check flag-based requirements first
  if (requireSecTeam && !isSecTeam) {
    return <Navigate to={redirectTo} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check role-based requirements
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};
